import Decimal from 'decimal.js';

import { STATE_CODE_TO_NAME } from '@/constants/states';

import {
  TaxDetail_FederalTaxDetailFragment,
  TaxDetail_GiftTaxDetailFragment,
  TaxDetail_GstTaxDetailFragment,
  TaxDetail_StateInheritanceTaxDetailFragment,
  TaxDetail_StateTaxDetailFragment,
} from './graphql/TaxDetail.generated';

export interface StateTaxFieldsOutput {
  /** state name */
  stateName: string | null | undefined;
  /** state tax due */
  stateTaxDue: Decimal;
  /** exemption used */
  exemptionUsed: Decimal;
  /** exemption available (CT specific) */
  exemptionAvailable: Decimal;
  /** gross estate, separate from any exemption or valuation discounts */
  grossEstate: Decimal;
  /** taxable estate - the amount of the estate that is subject to tax */
  taxableEstate: Decimal;
  /** adjusted taxable gifts - amount of taxable gifts made by the dying grantor (IL specific) */
  adjustedTaxableGifts: Decimal;
  /** CT taxes paid on prior gifts - cumulative CT gift taxes paid during grantor's lifetime (CT specific) */
  ctTaxesPaid: Decimal;
  /**
   * amount subject to tax, inclusive of valuation discount and exemption
   *
   * should be the difference of `taxableAmount - exemptionUsed - valuationDiscountApplied`
   */
  amountSubjectToTax: Decimal;
  /** valuation discount applied */
  valuationDiscountApplied: Decimal;
  /**
   * inheritance tax credited against this state's estate tax (MD-specific).
   * Zero when the state has no inheritance tax to credit.
   */
  inheritanceTaxCredit: Decimal;
  /** whether the taxable amount is over the cliff */
  isTaxableAmountOverCliff: boolean;
  /**
   * Charitable and marital bequests
   */
  nonTaxableTransfers: Decimal;
  /**
   * Pre-apportionment tax for fractional-apportionment states (e.g. CT, MN, OR, WA).
   * Null when apportionment does not apply.
   */
  tentativeStateTax: Decimal | null;
  /**
   * Fraction applied to the tentative tax for fractional-apportionment states (e.g. 0.87).
   * Null when apportionment does not apply.
   */
  apportionmentRatio: Decimal | null;
}

export function getStateTaxFields(
  stateTax: TaxDetail_StateTaxDetailFragment
): StateTaxFieldsOutput {
  return {
    stateName:
      STATE_CODE_TO_NAME[stateTax.stateCode ?? ''] || stateTax.stateCode,
    grossEstate: stateTax.grossEstate ?? new Decimal(0),
    taxableEstate: stateTax.taxableEstate ?? new Decimal(0),
    adjustedTaxableGifts: stateTax.adjustedTaxableGifts ?? new Decimal(0),
    ctTaxesPaid: stateTax.ctTaxesPaid ?? new Decimal(0),
    exemptionAvailable: stateTax.exemptionAvailable ?? new Decimal(0),
    stateTaxDue: stateTax.tax ?? new Decimal(0),
    exemptionUsed: stateTax.exemptionUsed ?? new Decimal(0),
    valuationDiscountApplied: stateTax.valuationDiscount ?? new Decimal(0),
    amountSubjectToTax: stateTax.taxableAmount ?? new Decimal(0),
    inheritanceTaxCredit: stateTax.inheritanceTaxCredit ?? new Decimal(0),
    isTaxableAmountOverCliff: stateTax.isTaxableAmountOverCliff ?? false,
    nonTaxableTransfers: stateTax.nonTaxableTransfers ?? new Decimal(0),
    tentativeStateTax: stateTax.tentativeStateTax ?? null,
    apportionmentRatio: stateTax.apportionmentRatio ?? null,
  };
}

export function getFederalTaxFields(
  federalTax:
    | TaxDetail_FederalTaxDetailFragment
    | TaxDetail_GiftTaxDetailFragment
) {
  const lifetimeExemptionUsed = federalTax.exemptionUsed ?? new Decimal(0);
  const federalTaxDue = federalTax.tax ?? new Decimal(0);

  let taxableEstate = new Decimal(0);
  let stateTaxDeduction = new Decimal(0);
  let gstTaxDeduction = new Decimal(0);
  let amountSubjectToTax = new Decimal(0);
  let valuationDiscountApplied = new Decimal(0);
  let nonTaxableTransfers = new Decimal(0);

  if ('grossEstate' in federalTax) {
    taxableEstate = federalTax.grossEstate ?? new Decimal(0);
  }

  if ('stateTaxDeductions' in federalTax) {
    stateTaxDeduction = federalTax.stateTaxDeductions ?? new Decimal(0);
  }

  if ('gstTaxDeductions' in federalTax) {
    gstTaxDeduction = federalTax.gstTaxDeductions ?? new Decimal(0);
  }

  if ('taxableAmount' in federalTax) {
    amountSubjectToTax = federalTax.taxableAmount ?? new Decimal(0);
  }

  if ('valuationDiscount' in federalTax) {
    valuationDiscountApplied = federalTax.valuationDiscount ?? new Decimal(0);
  }

  if ('nonTaxableTransfers' in federalTax) {
    nonTaxableTransfers = federalTax.nonTaxableTransfers ?? new Decimal(0);
  }

  return {
    taxableEstate,
    stateTaxDeduction,
    gstTaxDeduction,
    lifetimeExemptionUsed,
    amountSubjectToTax,
    federalTaxDue,
    valuationDiscountApplied,
    nonTaxableTransfers,
  };
}

interface BaseGstTaxFieldsOutput {
  /** taxable gifts made/transfers subject to GST tax */
  taxableGiftsMade: Decimal;
  /** valuation discount applied */
  valuationDiscountApplied?: Decimal;
  /** exemption used */
  exemptionUsed: Decimal;
  /** total transfer amount */
  amountSubjectToTax: Decimal;
  /** GST tax due */
  gstTaxDue: Decimal;
}

export interface GstGiftTaxFieldsOutput extends BaseGstTaxFieldsOutput {
  valuationDiscountApplied?: never;
}

export function getGstGiftTaxFields(
  taxSummary: TaxDetail_GiftTaxDetailFragment | null
): GstGiftTaxFieldsOutput {
  const { taxableGiftsMade, exemptionUsed } = taxSummary ?? {};

  // TODO get from BE
  let amountSubjectToTax = new Decimal(0);
  if (taxableGiftsMade && exemptionUsed) {
    amountSubjectToTax = taxableGiftsMade.minus(exemptionUsed);
  }

  return {
    taxableGiftsMade: taxableGiftsMade ?? new Decimal(0),
    exemptionUsed: exemptionUsed ?? new Decimal(0),
    amountSubjectToTax,
    gstTaxDue: taxSummary?.tax ?? new Decimal(0),
  };
}

export interface GstTaxFieldsOutput extends BaseGstTaxFieldsOutput {
  /** valuation discount applied */
  valuationDiscountApplied: Decimal;
}

export function getGstTaxFields(
  taxSummary: TaxDetail_GstTaxDetailFragment | null
): GstTaxFieldsOutput {
  return {
    taxableGiftsMade: taxSummary?.totalTransferAmount ?? new Decimal(0),
    valuationDiscountApplied: taxSummary?.valuationDiscount ?? new Decimal(0),
    exemptionUsed: taxSummary?.exemptionUsed ?? new Decimal(0),
    amountSubjectToTax: taxSummary?.taxableAmount ?? new Decimal(0),
    gstTaxDue: taxSummary?.tax ?? new Decimal(0),
  };
}

export interface StateInheritanceTaxFieldsOutput {
  /** state name */
  stateName: string | null | undefined;
  /** inheritance tax due */
  inheritanceTaxDue: Decimal;
  /** total transfer amount */
  totalTransferAmount: Decimal;
  /** valuation discount applied */
  valuationDiscountApplied: Decimal;
  /** per-beneficiary-class exemption applied for this state */
  exemptionUsed: Decimal;
  /** federal estate tax deducted from this state's inheritance-tax base */
  federalEstateTaxDeduction: Decimal;
  /** deduction for death taxes another (non-resident) state levied */
  nonResidentStateTaxDeduction: Decimal;
  /** amount subject to tax */
  amountSubjectToTax: Decimal;
}

export function getStateInheritanceTaxFields(
  inheritanceTax: TaxDetail_StateInheritanceTaxDetailFragment
): StateInheritanceTaxFieldsOutput {
  return {
    stateName:
      STATE_CODE_TO_NAME[inheritanceTax.stateCode ?? ''] ||
      inheritanceTax.stateCode,
    inheritanceTaxDue: inheritanceTax.tax ?? new Decimal(0),
    totalTransferAmount: inheritanceTax.totalTransferAmount ?? new Decimal(0),
    valuationDiscountApplied:
      inheritanceTax.valuationDiscount ?? new Decimal(0),
    exemptionUsed: inheritanceTax.exemptionUsed ?? new Decimal(0),
    federalEstateTaxDeduction:
      inheritanceTax.federalEstateTaxDeduction ?? new Decimal(0),
    nonResidentStateTaxDeduction:
      inheritanceTax.nonResidentStateTaxDeduction ?? new Decimal(0),
    amountSubjectToTax: inheritanceTax.taxableAmount ?? new Decimal(0),
  };
}
